/**
 * ч.958 (рішення власника): «Обліковий запис» — сторінка з розділами
 * (замість попапа у хідері). Маршрут /account/:section?.
 *
 * ПРАВА (ч.1104, рішення власника):
 *  • «Облікові дані», «Мої доступи», «Видалити акаунт» — кожному (це про саму людину);
 *  • «Oblik+/AI», «Підписка і оплата», «Сховище» — власник і адміністратор акаунта
 *    або право перегляду (`tenant.*.view`; за замовчуванням є в головного бухгалтера);
 *  • «Адміністрування бізнес-акаунта» — `admin.access.manage`;
 *  • «Передача організації», «Видалити організацію» — лише власник акаунта.
 * Режим «Користувач» без організацій (свій порожній акаунт): сторінки про
 * бізнес-акаунт не показуються взагалі — вони там ні до чого.
 * Прямий URL на недоступний розділ → перший доступний (чужих можливостей не «світимо»).
 */
import { useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { orgTransfers } from '@/api/accountOrgs';

import { usePermissions } from '@/auth/usePermissions';
import { useOnboarding } from '@/hooks/useOnboarding';
import AccountLayout, { type AccountSectionDef } from './AccountLayout';
import CredentialsSection from './CredentialsSection';
import MyAccessSection from './MyAccessSection';
import AiCreditsSection from './AiCreditsSection';
import StorageSection from './StorageSection';
import DeleteOrganizationSection from './DeleteOrganizationSection';
import DeleteAccountSection from './DeleteAccountSection';
import BillingSection from './BillingSection';
import TransferOrganizationSection from './TransferOrganizationSection';
import AdminPage from '@/pages/AdminPage';

/** Розділи про бізнес-акаунт (а не про саму людину) — у режимі «Користувач» без організацій зайві. */
export const BUSINESS_SECTIONS = new Set(['ai', 'subscription', 'storage', 'admin', 'transfer', 'delete-org']);
/** ч.1406 (рішення власника): у режимі «ЕДО» — без помічників, адміністрування і передачі організації. */
export const EDO_HIDDEN_SECTIONS = new Set(['ai', 'admin', 'transfer']);

export default function AccountPage() {
  const { section = '' } = useParams<{ section?: string }>();
  const perms = usePermissions();
  const { onboarding, edoOnly, ctx: modeCtx } = useOnboarding();
  const isOwner = perms.me?.tenant_role === 'OWNER';
  // ч.1406: «Видалити організацію» — усі власні акаунти (з картками ЕДО), тож розділ є і з чужого
  // акаунта (режим «Користувач»), якщо власний акаунт узагалі є.
  const ownsAccount = !!modeCtx?.personal_tenant_id;
  // ч.1107: якщо користувачу передають організацію — сторінку передачі видно завжди
  // (навіть не власнику і в режимі «Користувач»: прийняти треба десь побачити).
  const incomingQ = useQuery({ queryKey: ['org-transfers-incoming'], queryFn: orgTransfers.incoming,
    staleTime: 60_000 });
  const hasIncoming = (incomingQ.data?.transfers.length ?? 0) > 0;
  const canSend = !perms.isLoading && isOwner && !onboarding;

  // permission: null — усім; рядок — право (власник/адміністратор мають усе);
  // 'OWNER' — лише власник акаунта.
  const all: (AccountSectionDef & { ownerOnly?: boolean })[] = [
    { key: '', label: 'Облікові дані', permission: null, element: <CredentialsSection /> },
    // ч.1075: акаунти, організації і власні ярлики клієнтів.
    { key: 'access', label: 'Мої доступи', permission: null, element: <MyAccessSection /> },
    { key: 'ai', label: 'Oblik+/AI', permission: 'tenant.ai_credits.view', element: <AiCreditsSection /> },
    // ч.1398: баланс акаунта, тарифи баз, сховище, кредити — замість заглушки «Підписка і оплата».
    { key: 'subscription', label: 'Баланс і тарифи', permission: 'tenant.subscription.view',
      element: <BillingSection /> },
    { key: 'storage', label: 'Сховище', permission: 'tenant.storage.view', element: <StorageSection /> },
    { key: 'admin', label: 'Адміністрування бізнес-акаунта',
      permission: 'admin.access.manage', element: <AdminPage /> },
    { key: 'transfer', label: 'Передача організації', permission: null, ownerOnly: true,
      element: <TransferOrganizationSection canSend={canSend} /> },
    { key: 'delete-org', label: 'Видалити організацію', permission: null, ownerOnly: true,
      element: <DeleteOrganizationSection /> },
    { key: 'delete', label: 'Видалити акаунт', permission: null,
      element: <DeleteAccountSection /> },
  ];

  // Поки права вантажаться — показуємо лише «свої» розділи (без адмінських),
  // щоб пункти не блимали.
  const visible = all.filter((s) => {
    if (s.key === 'transfer' && hasIncoming) return true;
    if (s.key === 'delete-org' && ownsAccount) return !perms.isLoading;
    if (onboarding && BUSINESS_SECTIONS.has(s.key)) return false;
    if (edoOnly && EDO_HIDDEN_SECTIONS.has(s.key)) return false;
    if (s.ownerOnly) return !perms.isLoading && isOwner;
    return s.permission === null || (!perms.isLoading && perms.has(s.permission));
  });

  // ч.1080: міграція переїхала в меню «Головна → Міграція» (було «Міграція і налаштування» — старі адреси.
  if (section === 'migration') return <Navigate to="/migration/wizard" replace />;
  if (section === 'migration-runs') return <Navigate to="/migration/runs" replace />;

  // Прямий URL на недоступний розділ → на перший доступний.
  const exists = visible.some((s) => s.key === section);
  if (!exists && !perms.isLoading) return <Navigate to="/account" replace />;

  return <AccountLayout sections={visible} activeKey={section} />;
}
